import React from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  VStack,
  HStack,
  Icon,
  Text,
  Progress,
  Button,
} from '@chakra-ui/react';
import { FaCheckCircle, FaCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useUsers } from '../../hooks/useUsers';
import { useProjects } from '../../hooks/useProjects';
import { useClients } from '../../hooks/useClients';
import { useInvites } from '../../hooks/useInvites';
import { useTasks } from '../../hooks/useTasks';

const SetupChecklist: React.FC = () => {
  const navigate = useNavigate();
  const { data: users } = useUsers();
  const { data: projects } = useProjects(true);
  const { data: clients } = useClients();
  const { data: invites } = useInvites();
  const { data: tasks } = useTasks(true);

  const steps = [
    {
      id: 'client',
      label: 'Add your first client',
      done: (clients?.length ?? 0) > 0,
      action: () => navigate('/dashboard/clients'),
      cta: 'Add client',
    },
    {
      id: 'project',
      label: 'Create a project',
      done: (projects?.length ?? 0) > 0,
      action: () => navigate('/dashboard/projects'),
      cta: 'New project',
    },
    {
      id: 'team',
      label: 'Invite a teammate',
      done: (users?.length ?? 0) > 1 || (invites?.length ?? 0) > 0,
      action: () => navigate('/dashboard/users'),
      cta: 'Invite user',
    },
    {
      id: 'task',
      label: 'Create your first task',
      done: (tasks?.filter((t) => !t.isSubtask).length ?? 0) > 0,
      action: () => navigate('/dashboard/tasks'),
      cta: 'View tasks',
    },
  ];

  const completed = steps.filter((s) => s.done).length;
  const pct = Math.round((completed / steps.length) * 100);

  if (completed === steps.length) return null;

  return (
    <Card mb={8}>
      <CardHeader pb={2}>
        <Heading size="md">Get started</Heading>
        <Text fontSize="sm" color="gray.600" mt={1}>
          Complete these steps to set up your workspace
        </Text>
      </CardHeader>
      <CardBody pt={0}>
        <Progress value={pct} size="sm" borderRadius="full" mb={4} colorScheme="blue" />
        <VStack align="stretch" spacing={3}>
          {steps.map((step) => (
            <HStack key={step.id} justify="space-between">
              <HStack>
                <Icon
                  as={step.done ? FaCheckCircle : FaCircle}
                  color={step.done ? 'green.400' : 'gray.300'}
                />
                <Text
                  fontSize="sm"
                  textDecoration={step.done ? 'line-through' : undefined}
                  color={step.done ? 'gray.500' : undefined}
                >
                  {step.label}
                </Text>
              </HStack>
              {!step.done && (
                <Button size="xs" variant="outline" onClick={step.action}>
                  {step.cta}
                </Button>
              )}
            </HStack>
          ))}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default SetupChecklist;
